import { AxiosRequestConfig, AxiosResponse } from 'axios';
import { axiosClient } from 'config/Axios/AxiosInstanceConfigs';
import { formDataPostRequest, postRequest } from 'config/Axios/baseConfig';
import {
  AddOpportunityPayload,
  AddOpportunityResponse,
  ConceptSuggestions,
  ConceptResponse,
  UpdateBoardResponse,
  OpportunityDetail,
  MoleculeNames,
  DropdownDetails,
  updateOpportunity,
  Comment,
  ConceptPayload,
  SiteTransferResponse,
  SiteTransferInfo,
  SaveSiteTransferResponse,
  SaveSiteTransferNoteResponse,
  NADWaiverResponse,
  NADWaiverInfo,
  NADWaiverSaveResponse,
  NADWaiverNoteResponse,
  NADWaiverNoteRequest,
  CommentRequestPayload,
  SiteTransferInfoPayload,
  SiteTransferNotePayload,
  OpportunityDetailsResponseInterface,
  MoleculeSuggestionsResponseInterface,
  Supplier,
  portfolioOpportunitiesDataInterface,
  portfolioOpportunitiesSaveResponse,
  portfolioNdcStrengthPacksizeResponseInterface,
  PortfolioOpportunitiesDetailsInterface,
  portfolioOpportunitiesFileUploadResponseInterface,
} from 'Interfaces/BDPortalInterfaces';
import {
  AddIteractionCheckBoxRequestPayload,
  allIterationIterface,
  checkOpportunityExistsResponse,
  paralleIterationsResponseInterface,
} from 'Interfaces/BDPortalInterfaces/AddNewIterationInterfaces';
import { IterationHistoryInterface } from 'Interfaces/BDPortalInterfaces/HistoryInterface';

export async function getRequest<T>(
  url: string,
  token?: string,
  config?: AxiosRequestConfig & { signal?: AbortSignal } // Add signal to config
): Promise<AxiosResponse<T>> {
  const headers = token
    ? {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      }
    : {
        'Content-Type': 'application/json',
      };

  const response = await axiosClient.get<T>(`/${url}`, {
    headers,
    ...config, // Merge the provided config with any additional parameters
  });

  return response;
}

export const bdportalScreenServices = {
  getDropdownDetails: (signal?: AbortSignal): Promise<DropdownDetails> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<DropdownDetails>(
          `bd_opp_tracker/get_dimensions_drop_down`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getDropdownDetails, Err===', error);
        reject(error);
      }
    });
  },

  getMoleculeSuggestions: (
    query: string,
    signal?: AbortSignal
  ): Promise<MoleculeSuggestionsResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<MoleculeSuggestionsResponseInterface>(
          `bd_opp_tracker/get-molecule-suggestions`,
          { query },
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getMoleculeSuggestions, Err===', error);
        reject(error);
      }
    });
  },

  getSuppliersSuggestions: (
    query: string,
    signal: AbortSignal | undefined = undefined
  ): Promise<Supplier[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        if (signal) {
          const { data } = await postRequest<Supplier[]>(
            `bd_opp_tracker/get_supplier_suggestions`,
            { query },
            undefined,
            { signal }
          );
          resolve(data);
        } else {
          const { data } = await postRequest<Supplier[]>(
            `bd_opp_tracker/get_supplier_suggestions`,
            { query }
          );
          resolve(data);
        }
      } catch (error) {
        console.log('in services > getMoleculeSuggestions, Err===', error);
        reject(error);
      }
    });
  },

  getStrengthSuggestions: (
    payload: { molecule: string },
    signal: AbortSignal | undefined = undefined
  ): Promise<string[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        if (signal) {
          const { data } = await postRequest<string[]>(
            `bd_opp_tracker/molecule-wise-strenth-suggestions`,
            payload,
            undefined,
            { signal }
          );
          resolve(data);
        } else {
          const { data } = await postRequest<string[]>(
            `bd_opp_tracker/molecule-wise-strenth-suggestions`,
            payload
          );
          resolve(data);
        }
      } catch (error) {
        console.log('in services > getStrengthSuggestions, Err===', error);
        reject(error);
      }
    });
  },

  getAllMoleculeDetails: (): Promise<MoleculeNames[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<MoleculeNames[]>(
          `bd_opp_tracker/get_all_active_opportunity`
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getAllMoleculeDetails, Err===', error);
        reject(error);
      }
    });
  },

  addOpportunity: (addOpportunity: AddOpportunityPayload): Promise<AddOpportunityResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<AddOpportunityResponse>(
          `bd_opp_tracker/add_new_opportunity`,
          addOpportunity
        );
        resolve(data);
      } catch (err) {
        console.log('in services > addOpportunity, Err===', err);
        reject(err);
      }
    });
  },

  getAllOpportunity: (boardType: string, signal?: AbortSignal): Promise<OpportunityDetail[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<OpportunityDetail[]>(
          `bd_opp_tracker/get_all_opportunity/${boardType}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getAllOpportunity, Err===', error);
        reject(error);
      }
    });
  },

  getAllPortfolioOpportunities: (
    boardType: string,
    signal?: AbortSignal
  ): Promise<PortfolioOpportunitiesDetailsInterface[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<PortfolioOpportunitiesDetailsInterface[]>(
          `bd_opp_tracker/get_all_portfolio_opportunities/${boardType}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getAllPortfolioOpportunities, Err===', error);
        reject(error);
      }
    });
  },

  updateBoard: (
    updateNCBoard: updateOpportunity,
    boardType: string
  ): Promise<UpdateBoardResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<UpdateBoardResponse>(
          `bd_opp_tracker/update_board/${boardType}`,
          updateNCBoard
        );
        resolve(data);
      } catch (err) {
        console.log('in services > updateBoard, Err===', err);
        reject(err);
      }
    });
  },

  bulkUpdateBoard: (
    updateNCBoard: updateOpportunity[],
    boardType: string
  ): Promise<{ message: string }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<{ message: string }>(
          `bd_opp_tracker/bulk_update_board/${boardType}`,
          updateNCBoard
        );
        resolve(data);
      } catch (err) {
        console.log('in services > bulkUpdateBoard, Err===', err);
        reject(err);
      }
    });
  },

  getOpportunityByID: (
    OpportunityIterationsID: number,
    signal?: AbortSignal
  ): Promise<OpportunityDetailsResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<OpportunityDetailsResponseInterface>(
          `bd_opp_tracker/get_opp_details/${OpportunityIterationsID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getOpportunityByID, Err===', error);
        reject(error);
      }
    });
  },

  getCommentsByOppID: (OpportunityID: number, signal?: AbortSignal): Promise<Comment[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<Comment[]>(
          `bd_opp_tracker/get_opp_comments/${OpportunityID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getCommentsByOppID, Err===', error);
        reject(error);
      }
    });
  },

  getConceptByID: (
    OpportunityIterationsID: number,
    signal?: AbortSignal
  ): Promise<ConceptResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<ConceptResponse>(
          `bd_opp_tracker/get_opp_concept_info/${OpportunityIterationsID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getConceptByID, Err===', error);
        reject(error);
      }
    });
  },

  getConceptByOppID: (
    OpportunityID: number,
    signal?: AbortSignal
  ): Promise<ConceptSuggestions[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<ConceptSuggestions[]>(
          `bd_opp_tracker/get_concepts/${OpportunityID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getConceptByOppID, Err===', error);
        reject(error);
      }
    });
  },

  saveConcept: (payload: ConceptPayload[]): Promise<ConceptPayload[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<ConceptPayload[]>(
          `bd_opp_tracker/save_opp_concept_info`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveConcept, Err===', err);
        reject(err);
      }
    });
  },

  saveComments: (payload: CommentRequestPayload): Promise<Comment[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<Comment[]>(`bd_opp_tracker/save_opp_comments`, [
          payload,
        ]);
        resolve(data);
      } catch (err) {
        console.log('in services > saveComments, Err===', err);
        reject(err);
      }
    });
  },

  createNewIteration: (
    payload: AddIteractionCheckBoxRequestPayload
  ): Promise<{
    Message: string;
    iterationId: string;
    opportunityId: string;
  }> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<{
          Message: string;
          iterationId: string;
          opportunityId: string;
        }>(`bd_opp_tracker/create_new_iteration`, payload);
        resolve(data);
      } catch (err) {
        console.log('in services > createNewIteration, Err===', err);
        reject(err);
      }
    });
  },

  getSiteTransferByID: (
    OpportunityIID: number,
    signal?: AbortSignal
  ): Promise<SiteTransferResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<SiteTransferResponse>(
          `bd_opp_tracker/get_site_transfor_info/${OpportunityIID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getSiteTransferByID, Err===', error);
        reject(error);
      }
    });
  },

  saveSiteTransfer: (payload: SiteTransferInfo): Promise<SaveSiteTransferResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveSiteTransferResponse>(
          `bd_opp_tracker/save_opp_comments`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransfer, Err===', err);
        reject(err);
      }
    });
  },

  saveSiteTransferNote: (
    payload: SiteTransferNotePayload
  ): Promise<SaveSiteTransferNoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveSiteTransferNoteResponse>(
          `bd_opp_tracker/save_site_transfor_notes`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransferNote, Err===', err);
        reject(err);
      }
    });
  },

  saveSiteTransferDetails: (
    payload: SiteTransferInfoPayload
  ): Promise<SaveSiteTransferNoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<SaveSiteTransferNoteResponse>(
          `bd_opp_tracker/save_site_transfor_info`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransferDetails, Err===', err);
        reject(err);
      }
    });
  },

  getNADWaiverByID: (OpportunityIID: number, signal?: AbortSignal): Promise<NADWaiverResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<NADWaiverResponse>(
          `bd_opp_tracker/get_nad_waiver_info/${OpportunityIID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getNADWaiverByID, Err===', error);
        reject(error);
      }
    });
  },

  saveNADWaiverDetails: (payload: NADWaiverInfo): Promise<NADWaiverSaveResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<NADWaiverSaveResponse>(
          `bd_opp_tracker/save_nad_waiver_info`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransferDetails, Err===', err);
        reject(err);
      }
    });
  },

  saveNADWaiverNote: (payload: NADWaiverNoteRequest): Promise<NADWaiverNoteResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<NADWaiverNoteResponse>(
          `bd_opp_tracker/save_nad_waiver_notes`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransferDetails, Err===', err);
        reject(err);
      }
    });
  },

  getIterationHistory: (
    iterationID: number,
    signal?: AbortSignal
  ): Promise<IterationHistoryInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<IterationHistoryInterface>(
          `bd_opp_tracker/get_history/${iterationID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in bd portal service > getIterationHistory, Err===', error);
        reject(error);
      }
    });
  },

  getAllIterationsForOpportunity: (
    opportunityID: number,
    signal?: AbortSignal
  ): Promise<allIterationIterface[]> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<allIterationIterface[]>(
          `bd_opp_tracker/get_all_iterations_details/${opportunityID}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in bd portal service > getAllIterationsForOpportunity, Err=== ', error);
        reject(error);
      }
    });
  },

  getParallelIterations: (payload: {
    moleculeAndForm: string;
  }): Promise<paralleIterationsResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<paralleIterationsResponseInterface>(
          `bd_opp_tracker/get-all-parallel-iterations`,
          payload
        );
        resolve(data);
      } catch (err) {
        console.log('in services > saveSiteTransferDetails, Err===', err);
        reject(err);
      }
    });
  },

  checkOpportunityAlreadyExistsForMoleculeOrNot: ({
    payload,
    signal,
  }: {
    payload: {
      molecule_and_form: string;
    };
    signal?: AbortSignal;
  }): Promise<checkOpportunityExistsResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<checkOpportunityExistsResponse>(
          `bd_opp_tracker/check-opportunity-exists-for-molecule`,
          payload,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in bd portal service > getAllIterationsForOpportunity, Err=== ', error);
        reject(error);
      }
    });
  },

  getPortfolioOpportunitiesPreviewData: (
    formData: FormData
  ): Promise<portfolioOpportunitiesFileUploadResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } =
          await formDataPostRequest<portfolioOpportunitiesFileUploadResponseInterface>(
            `bd_opp_tracker/portfolio-opportunities-preview-data`,
            formData
          );
        resolve(data);
      } catch (error) {
        console.log('in services > getPortfolioOpportunitiesPreviewData, Err ===', error);
        reject(error);
      }
    });
  },

  savePortfolioOpportunities: (
    payload: portfolioOpportunitiesDataInterface
  ): Promise<portfolioOpportunitiesSaveResponse> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await postRequest<portfolioOpportunitiesSaveResponse>(
          `bd_opp_tracker/portfolio-opportunities-save`,
          payload
        );
        resolve(data);
      } catch (error) {
        console.log('in services > savePortfolioOpportunities, Err ===', error);
        reject(error);
      }
    });
  },

  getPacksizeNdcStrengthForPortfolioOpportunties: (
    itertaion_id: number,
    signal?: AbortSignal
  ): Promise<portfolioNdcStrengthPacksizeResponseInterface> => {
    return new Promise(async (resolve, reject) => {
      try {
        const { data } = await getRequest<portfolioNdcStrengthPacksizeResponseInterface>(
          `bd_opp_tracker/get-iteration-source-packsize-ndc-strength/${itertaion_id}`,
          undefined,
          { signal }
        );
        resolve(data);
      } catch (error) {
        console.log('in services > getAllOpportunity, Err===', error);
        reject(error);
      }
    });
  },
};
